<template>
    <div class="flex flex-col justify-center text-white">
        <h2>Demos</h2>
        <template v-for="group in groups" :key="group.label">
            <!-- The rules either side of the label are pure decoration. -->
            <div class="flex items-center my-10 md:my-14">
                <span class="flex-grow border-t border-white/40" aria-hidden="true"></span>
                <h3 class="mx-4 uppercase tracking-widest text-sm md:text-base">{{ group.label }}</h3>
                <span class="flex-grow border-t border-white/40" aria-hidden="true"></span>
            </div>
            <div class="md:text-lg song-columns">
                <div v-for="song in group.songs" :key="song.file" class="mb-2 flex justify-between">
                    <div class="flex items-center">
                        <!--
                            aria-pressed makes this a toggle rather than two
                            separate controls, so the state is announced on the
                            same button the user just activated. The icons carry
                            no alt text: the button's label already names both
                            the action and the sample.
                        -->
                        <button
                            type="button"
                            @click="toggleSong(song)"
                            class="play-toggle w-3 h-4 mr-3 flex-shrink-0 flex items-center cursor-pointer"
                            :class="{'opacity-50': isActive(song) && buffering}"
                            :aria-label="`${isActive(song) ? 'Pause' : 'Play'} ${song.title}`"
                            :aria-pressed="isActive(song)"
                            :title="isActive(song) ? 'Pause' : 'Play'"
                        >
                            <!--
                                Both icons stay in the DOM (v-show, not v-if) so
                                they are fetched with the page. Swapping them in
                                would otherwise leave the box without an image
                                for a moment, collapsing it to zero height - the
                                control then disappears mid-tap on slow
                                connections and the second tap misses it.
                            -->
                            <img
                                v-show="!isActive(song)"
                                class="w-full"
                                src="../assets/icons/play.svg"
                                alt=""
                            >
                            <img
                                v-show="isActive(song)"
                                class="w-full"
                                src="../assets/icons/pause.svg"
                                alt=""
                            >
                        </button>
                        <span>{{ song.title }}</span>
                    </div>
                    <span>{{ song.duration }}</span>
                </div>
            </div>
        </template>
        <!--
            A single, always-present audio element is reused for every sample.
            Mobile browsers unlock playback per element and only inside a user
            gesture, so one element that is played straight from the click
            handler is the only reliably unlockable setup.
        -->
        <audio
            ref="player"
            preload="none"
            playsinline
            @play="onPlay"
            @pause="onPause"
            @ended="onEnded"
            @waiting="buffering = true"
            @playing="buffering = false"
            @error="onError"
        ></audio>
    </div>
</template>

<script>
    export default {
        name: "Work",
        data() {
            return {
                groups: [
                    {
                      label: 'English',
                      songs: [
                        {
                          title: 'Commercial - Energetic',
                          file: 'Commercial_Energetic.mp3',
                          duration: '0:35',
                        },
                        {
                          title: 'Commercial - Sensual',
                          file: 'Commercial_Car_Axiom.mp3',
                          duration: '0:38',
                        },
                        {
                          title: 'Commercial - Hopeful',
                          file: 'Introducing_Newtide.mp3',
                          duration: '0:47',
                        },
                        {
                          title: 'Acting Dialogue - Confronting',
                          file: 'Acting_Dialogue_Confronting.mp3',
                          duration: '1:23',
                        },
                        {
                          title: 'Acting Dialogue - Emotional',
                          file: 'Acting_Dialogue_Dying.mp3',
                          duration: '1:23',
                        },
                        {
                          title: 'Acting Dialogue - Sensual',
                          file: 'Audiobook_English.mp3',
                          duration: '0:58',
                        },
                      ],
                    },
                    {
                        label: 'German',
                        songs: [
                            {
                                title: 'Commercial - Leiner',
                                file: 'Werbung_Leiner.mp3',
                                duration: '0:36',
                            },
                            {
                                title: 'Commercial - LG',
                                file: 'Werbung_LG.mp3',
                                duration: '0:18',
                            },
                            {
                                title: 'Commercial - Max Factor',
                                file: 'Werbung_Max_Factor.mp3',
                                duration: '0:25',
                            },
                            {
                                title: 'Commercial - Creamy Chocolate',
                                file: 'Werbung_Creamy_Chocolate.mp3',
                                duration: '0:36',
                            },
                            {
                                title: 'Explainer Video',
                                file: 'Erklärvideo.mp3',
                                duration: '0:48',
                            },
                            {
                                title: 'Corporate Film',
                                file: 'Imagefilm.mp3',
                                duration: '1:19',
                            },
                            {
                                title: 'Documentary - Elephants',
                                file: 'Dokumentation_Elefanten.mp3',
                                duration: '0:46',
                            },
                            {
                                title: 'Documentary - Universe',
                                file: 'Dokumentation_Universum.mp3',
                                duration: '0:54',
                            },
                            {
                                title: 'Voice Over',
                                file: 'Voice_Over.mp3',
                                duration: '1:14',
                            },
                            {
                                title: 'Audiobook',
                                file: 'Hörbuch.mp3',
                                duration: '1:24',
                            },
                            {
                                title: 'Audiobook - Excerpt',
                                file: 'Hörbuch_Auszug.mp3',
                                duration: '0:57',
                            },
                            {
                                title: 'Dubbing - Agitated',
                                file: 'Synchron_aufgebracht.mp3',
                                duration: '0:56',
                            },
                            {
                                title: 'Dubbing - Distraught/Panicked',
                                file: 'Synchron_aufgewühlt_panisch.mp3',
                                duration: '0:46',
                            },
                            {
                                title: 'Dubbing - Assertive/Strong',
                                file: 'Synchron_bestimmt_stark.mp3',
                                duration: '1:19',
                            },
                            {
                                title: 'Dubbing - Psycho',
                                file: 'Synchron_psycho.mp3',
                                duration: '1:19',
                            },
                            {
                              title: 'Book Teaser - Young Adult Novel',
                              file: 'Buchteaser_Jugendroman.mp3',
                              duration: '0:46',
                            },
                            {
                                title: 'Voice Colors & Accents',
                                file: 'Stimmfarben_und_Akzente.mp3',
                                duration: '3:04',
                            },
                            {
                              title: 'Answering Machine - German/English',
                              file: 'AB_Deutsch_Englisch.mp3',
                              duration: '1:18',
                            },
                        ],
                    },
                ],
                // Which sample is loaded into the player, and what the player is
                // actually doing. Both are only ever written from the audio
                // element's own events, never optimistically on click, so the
                // icons can't get out of sync with the real playback state.
                activeFile: null,
                playing: false,
                buffering: false,
            }
        },
        methods: {
            isActive(song) {
                return this.activeFile === song.file && this.playing;
            },
            toggleSong(song) {
                const player = this.$refs.player;

                if (this.activeFile === song.file) {
                    if (player.paused) {
                        this.start(player);
                    } else {
                        player.pause();
                    }
                    return;
                }

                // pause() takes effect immediately, so the previous sample can
                // never keep playing once another one is started.
                player.pause();
                this.activeFile = song.file;
                this.buffering = true;
                player.src = require(`../assets/audio/${song.file}`);
                this.start(player);
            },
            start(player) {
                // play() has to be called synchronously from the click handler:
                // as soon as it is deferred to a load/ready callback, mobile
                // browsers no longer count it as a user gesture and reject it.
                const started = player.play();

                if (started && typeof started.catch === 'function') {
                    started.catch(err => {
                        // AbortError just means we interrupted this play() with a
                        // pause() or a source switch - that is expected.
                        if (err && err.name === 'AbortError') {
                            return;
                        }
                        this.playing = false;
                        this.buffering = false;
                    });
                }
            },
            onPlay() {
                this.playing = true;
            },
            onPause() {
                this.playing = false;
                this.buffering = false;
            },
            onEnded() {
                this.playing = false;
                this.buffering = false;
                this.activeFile = null;
            },
            onError() {
                this.playing = false;
                this.buffering = false;
            },
        }
    }
</script>

<style scoped>
    /* Fill the two columns top-to-bottom then across (column-major), so the
       list reads 1,2 down the left column and 3,4 down the right - rather than
       CSS grid's row-major 1,2 / 3,4. Multi-column also avoids having to know
       the (per-group, variable) row count up front. */
    .song-columns > div {
        -webkit-column-break-inside: avoid;
        break-inside: avoid;
    }

    @media (min-width: 768px) {
        .song-columns {
            column-count: 2;
            column-gap: 6rem;
        }
    }

    .play-toggle {
        position: relative;
    }

    /* The icon itself is only 12px wide, which is far too small to hit
       reliably on a phone. This gives it a finger-sized tap area without
       changing the layout. */
    .play-toggle::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 2.5rem;
        height: 2.5rem;
        transform: translate(-50%, -50%);
    }
</style>
